import { Component } from '@theme/component';

/**
 * Usage tips section custom element.
 *
 * Canonical Swiper pattern (matches ax-tabbed-product-lists, ax-product-
 * recommendations, etc.): `<swiper-container init="false">` in Liquid, this
 * component calls `.initialize()` once at connect with breakpoints config.
 *
 *   mobile (< 750px): 1 slide visible, pagination dots, swipe enabled
 *   desktop (>= 750px): 3 slides side-by-side, swipe + pagination disabled
 *
 * @extends {Component}
 */
class AxUsageTips extends Component {
	/** @type {any} */
	#swiper = null;

	async connectedCallback() {
		super.connectedCallback();
		await customElements.whenDefined('swiper-container');
		this.#initSwiper();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		try {
			this.#swiper?.destroy(true, true);
		} catch (_) {
			// Swiper can throw during teardown if already destroyed.
		}
		this.#swiper = null;
	}

	#initSwiper() {
		const swiperEl = /** @type {HTMLElement & { swiper: any; initialize: () => void }} */ (
			this.querySelector('swiper-container.ax-usage-tips__swiper')
		);
		if (!swiperEl || swiperEl.swiper) return;

		const paginationEl = this.querySelector('.ax-usage-tips__pagination');

		Object.assign(swiperEl, {
			slidesPerView: 1,
			spaceBetween: 0,
			grabCursor: true,
			a11y: true,
			keyboard: { enabled: true },
			// External pagination target (light DOM div after the swiper).
			pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
			// When fewer slides than slidesPerView (e.g. single-tip variant on
			// desktop), center the slide(s) instead of left-aligning them.
			centerInsufficientSlides: true,
			breakpoints: {
				990: {
					slidesPerView: 3,
					spaceBetween: 0,
					allowTouchMove: false,
					pagination: false,
				},
			},
		});

		swiperEl.initialize();
		swiperEl.removeAttribute('init');
		this.#swiper = swiperEl.swiper;

		// Preload all CFH-lazy icons across all slides. In a horizontal carousel
		// the IntersectionObserver can't detect off-viewport slides, so we rely
		// on this dispatch (canonical Horizon pattern from observer-CFH.liquid)
		// to force-load every [data-lazy-keep] descendant right after init.
		document.dispatchEvent(
			new CustomEvent('removeLazyKeep', {
				detail: { lazyParentElement: swiperEl },
			}),
		);
	}
}

if (!customElements.get('ax-usage-tips')) {
	customElements.define('ax-usage-tips', AxUsageTips);
}
